import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { Loader2, Inbox, ShieldCheck } from 'lucide-react';
import { adminListApplications } from '../lib/api';

// Coach approval requirement was removed (Jun 2026). This tab is now a
// read-only signup log. Use the Coaches tab (with Ban / Delete actions)
// to remove bad actors.

const STATUS_TONE = {
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const DISCIPLINE_LABELS = {
  jiujitsu: 'Brazilian Jiu-Jitsu',
  self_defense: 'Self-Defense',
  personal_training: 'Personal Training',
};

const SignupRow = ({ app }) => (
  <div
    className="bg-[#18181B] border border-zinc-800 rounded-lg p-5"
    data-testid={`application-${app.id}`}
  >
    <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
      <div className="min-w-0">
        <p className="text-white font-semibold">{app.full_name}</p>
        <p className="text-xs text-zinc-500 mt-0.5 break-all">
          {app.email}
          {app.phone ? ` · ${app.phone}` : ''}
          {app.city ? ` · ${app.city}` : ''}
        </p>
        <p className="text-[11px] text-zinc-600 mt-1">
          Signed up {(app.created_at || '').slice(0, 10)}
          {app.source ? ` · ${app.source.replace(/_/g, ' ')}` : ''}
        </p>
      </div>
      <Badge className={`${STATUS_TONE[app.status] || ''} text-xs flex items-center gap-1`}>
        {app.auto_approved && <ShieldCheck className="w-3 h-3" />}
        {app.auto_approved ? 'auto-approved' : app.status}
      </Badge>
    </div>

    <div className="grid sm:grid-cols-2 gap-3 text-sm">
      {(app.disciplines || []).length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Disciplines</p>
          <p className="text-zinc-300">
            {(app.disciplines || []).map((d) => DISCIPLINE_LABELS[d] || d).join(', ')}
          </p>
        </div>
      )}
      {Number(app.years_experience) > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Experience</p>
          <p className="text-zinc-300">{app.years_experience} years</p>
        </div>
      )}
      {app.credentials && (
        <div className="sm:col-span-2">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Credentials</p>
          <p className="text-zinc-300 break-words">{app.credentials}</p>
        </div>
      )}
      {app.bio && (
        <div className="sm:col-span-2">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Bio</p>
          <p className="text-zinc-300 leading-relaxed whitespace-pre-line break-words">{app.bio}</p>
        </div>
      )}
    </div>
  </div>
);

const AdminApplications = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListApplications(null);
      setApps(res.data || []);
    } catch (e) {
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-applications">
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 mb-5 text-sm text-emerald-200">
        <p className="font-semibold mb-1">No approval required.</p>
        <p className="text-emerald-300/80 text-xs leading-relaxed">
          Coaches are auto-approved on signup. Use the <strong>Coaches</strong> tab to ban or
          delete bad actors. This list is a read-only log of who has signed up to coach.
        </p>
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-white">Coach signups</h2>
        <p className="text-xs text-zinc-500">{apps.length} total</p>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm py-10 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : apps.length === 0 ? (
        <div className="bg-[#18181B] border border-zinc-800 rounded-lg py-12 text-center" data-testid="no-applications">
          <Inbox className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No coach signups yet.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {apps.map((a) => (
            <SignupRow key={a.id} app={a} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminApplications;
